import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Tag, Typography, theme } from 'antd';
import type { PublicationPremiereDetailOut } from '@/api/generated/schemas';
import { PresencePill } from '@/components/common/PresencePill';
import { PublicationIconLink } from '@/components/user_catalog/publications/PublicationIconLink';
import { CoverThumb } from '@/components/common/show/CoverThumb';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import { ExclusivityTag } from '@/components/user_catalog/publications/ExclusivityTag';
import { personLine } from '@/components/user_catalog/publications/personLine';
import { availabilityStatusColors } from '@/pages/user_catalog/publications/constants';
import { formatDate, formatThousands } from '@/utils/format';
import { PREMIERE_SHOW_TABS, RESOURCE } from '../constants';
import type { PremiereShowTab } from '../constants';
import { countdownKey, daysUntilPremiere } from '../premiereCountdown';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<PremiereShowTab, string> = {
  general: 'base.sections.basic_info',
};

interface PublicationPremiereShowHeaderProps {
  record: PublicationPremiereDetailOut;
  activeTab: PremiereShowTab;
  onTabChange: (key: string) => void;
}

export const PublicationPremiereShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: PublicationPremiereShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const f = (key: string) => t(`${RESOURCE}.fields.${key}`);

  const days = daysUntilPremiere(record.published_at);

  const authors = personLine(
    record.publication_authors?.map((a) => ({
      position: a.position,
      name: a.author?.name,
    }))
  );
  const lectors = personLine(
    record.publication_lectors?.map((l) => ({
      position: l.position,
      name: l.lector?.name,
    }))
  );
  // Same "authors · read by lectors" line as the publication hero.
  const personsLine = [
    authors,
    lectors &&
      t('user_catalog.publications.show.hero.read_by', { names: lectors }),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <CoverThumb url={record.cover_url} size={128} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="center" wrap>
            {/* The icon opens the publication itself. */}
            <PublicationIconLink publicationId={record.id}>
              <Title
                level={3}
                style={{
                  margin: 0,
                  fontSize: token.fontSizeHeading3,
                  overflowWrap: 'anywhere',
                }}
              >
                {record.title}
              </Title>
            </PublicationIconLink>
            <Tag
              color={availabilityStatusColors[record.availability_status]}
              style={{ marginInlineEnd: 0 }}
            >
              {t(
                `user_catalog.publications.availability_status.${record.availability_status}`
              )}
            </Tag>
            <PresencePill
              present={record.is_premiere}
              label={t(
                `${RESOURCE}.followable.${record.is_premiere ? 'yes' : 'no'}`
              )}
              tooltip={t(`${RESOURCE}.followable.tooltip`)}
            />
            {/* The premiere projection carries no window, so no date tooltip. */}
            <ExclusivityTag isExclusive={record.is_exclusive} />
          </Flex>

          {personsLine && (
            <Text
              type="secondary"
              style={{ display: 'block', marginTop: token.marginXXS }}
            >
              {personsLine}
            </Text>
          )}

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id} · ${record.slug}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell size="lg" label={f('published_at')}>
              {orDash(formatDate(record.published_at))}
            </StatCell>
            <StatCell size="lg" label={f('days_until')}>
              {orDash(
                days != null &&
                  t(`${RESOURCE}.countdown.${countdownKey(days)}`, {
                    count: days,
                  })
              )}
            </StatCell>
            <StatCell size="lg" label={f('followers_count')}>
              {formatThousands(record.followers_count)}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={PREMIERE_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
